import type { FormEvent } from 'react';
import { makeAutoObservable, observable } from 'mobx';

import Settings from 'settings';
import { authenticateUser, LoginArgs } from 'core/api';
import { createCookies } from 'core/cookies';
import { i18n } from 'core/localization';
import { apiFetch } from 'framework/api';
import { ApplicationState } from 'framework/applicationState';
import { ModelValidator } from 'framework/mobx-integration';
import { redirectTo } from 'tools/navigation';

export class LoginFormStore {
	model = observable<LoginArgs>({
		username: '',
		password: '',
	});
	validator = new ModelValidator(this.model);

	showPassword = false;
	loading = false;
	failed = false;
	errorMessage: string = null;

	constructor() {
		makeAutoObservable(this);

		this.validator.required('username').required('password');
	}

	get toggleLabel() {
		return this.showPassword ? i18n('Hide password') : i18n('Show password');
	}

	toggleShowPassword = () => {
		this.showPassword = !this.showPassword;
	};

	onSubmit = (e: FormEvent) => {
		e.preventDefault();
		void this.submit();
	};

	async submit() {
		this.validator.validate();
		if (!this.validator.valid) {
			return;
		}

		this.loading = true;
		this.failed = false;
		this.errorMessage = null;

		try {
			const request = authenticateUser({ ...this.model });
			request.suppressErrorMessage = true;

			const response = await apiFetch(request);

			if (response.success) {
				createCookies(response.data);
				ApplicationState.loadStateFromCookies();
				await redirectTo(Settings.httpRoot, true);
				return;
			}

			if (response.message == 'wizard') {
				window.location.href = 'Wizard.jsp';
				return;
			}

			if (response.message == 'umchpwd') {
				window.location.href = 'ChangePassword.jsp?user=' + encodeURIComponent(this.model.username);
				return;
			}

			this.fail(response.message);
		} catch {
			this.fail();
		}
	}

	fail(message?: string) {
		this.failed = true;
		// apiFetch puts the raw body into message when the response is not JSON (e.g. a proxy error page)
		this.errorMessage = message?.trimStart().startsWith('<') ? null : message;
		this.model.password = '';
		this.loading = false;
	}

	destroy() {
		this.validator.destroy();
	}
}
